// components/RefurbishedPhones/RefurbishedPhonesList.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Smartphone, 
  Star, 
  Battery, 
  HardDrive, 
  Palette,
  Zap,
  Shield,
  ArrowRight,
  TrendingUp,
  Clock,
  CheckCircle
} from 'lucide-react';
import { summaryApi } from '../../common';

const RefurbishedPhonesList = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [phones, setPhones] = useState([]);
  const [filteredPhones, setFilteredPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    condition: '',
    brand: '',
    minPrice: '',
    maxPrice: '',
    inStock: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const searchRef = useRef(null);

  // Fetch refurbished phones
  const fetchPhones = async () => {
    try {
      setLoading(true);
      const response = await fetch(summaryApi.getRefurbishedPhones.url, {
        method: summaryApi.getRefurbishedPhones.method,
        
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPhones(data.data);
        setFilteredPhones(data.data);
      }
    } catch (error) {
      console.error('Error fetching refurbished phones:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and search
  useEffect(() => {
    let results = phones;

    // Search filter
    if (searchTerm) {
      results = results.filter(phone =>
        phone.phoneName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        phone.phoneBrand.toLowerCase().includes(searchTerm.toLowerCase()) ||
        phone.phoneModel.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Condition filter
    if (filters.condition) {
      results = results.filter(phone => phone.condition === filters.condition);
    }

    // Brand filter
    if (filters.brand) {
      results = results.filter(phone => phone.phoneBrand === filters.brand);
    }

    // Price filters
    if (filters.minPrice) {
      results = results.filter(phone => phone.phonePrice >= parseInt(filters.minPrice));
    }
    if (filters.maxPrice) {
      results = results.filter(phone => phone.phonePrice <= parseInt(filters.maxPrice));
    }

    // Stock filter
    if (filters.inStock) {
      results = results.filter(phone => phone.inStock);
    }

    setFilteredPhones(results);
  }, [searchTerm, filters, phones]);

  // Get unique brands for filter
  const uniqueBrands = [...new Set(phones.map(phone => phone.phoneBrand))];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'Like New': return '#10B981';
      case 'Excellent': return '#059669';
      case 'Good': return '#D97706';
      case 'Fair': return '#DC2626';
      default: return colors.textSecondary;
    }
  };

  const getConditionBadge = (condition) => {
    const color = getConditionColor(condition);
    return (
      <span
        className="px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 w-fit"
        style={{
          backgroundColor: `${color}20`,
          color: color
        }}
      >
        <CheckCircle size={12} />
        {condition}
      </span>
    );
  };

  useEffect(() => {
    fetchPhones();
  }, []);

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: colors.bg }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl mb-6"
            style={{ backgroundColor: `${colors.primary}15` }}
          >
            <Zap className="w-6 h-6" style={{ color: colors.primary }} />
            <span className="text-lg font-bold" style={{ color: colors.primary }}>
              Certified Refurbished
            </span>
          </motion.div>

          <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ color: colors.text }}>
            Premium Refurbished Phones
          </h1>
          <p className="text-xl max-w-2xl mx-auto mb-8 leading-relaxed" style={{ color: colors.textSecondary }}>
            Like-new devices with rigorous testing, warranty included, and significant savings
          </p>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap justify-center gap-6"
          >
            {[
              { icon: Shield, text: '1-Year Warranty' },
              { icon: CheckCircle, text: 'Quality Tested' },
              { icon: TrendingUp, text: 'Like New Condition' },
              { icon: Clock, text: '30-Day Return' }
            ].map((badge, index) => (
              <motion.div
                key={badge.text}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="flex items-center gap-2 px-4 py-2 rounded-full"
                style={{ backgroundColor: colors.bgSecondary }}
              >
                <badge.icon size={16} style={{ color: colors.primary }} />
                <span className="text-sm font-medium" style={{ color: colors.text }}>
                  {badge.text}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8 space-y-4"
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search 
                className="absolute left-4 top-1/2 transform -translate-y-1/2" 
                size={20} 
                style={{ color: colors.textSecondary }} 
              />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search by brand, model, or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-2xl outline-none transition-all focus:shadow-lg"
                style={{
                  backgroundColor: colors.bgSecondary,
                  color: colors.text,
                  border: `2px solid ${colors.border}`
                }}
              />
            </div>

            {/* Filter Toggle */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowFilters(!showFilters)}
              className="px-6 py-4 rounded-2xl font-semibold flex items-center gap-2 transition-all"
              style={{
                backgroundColor: showFilters ? colors.primary : colors.bgSecondary,
                color: showFilters ? 'white' : colors.text,
                border: `2px solid ${colors.border}`
              }}
            >
              <Filter size={20} />
              Filters
            </motion.button>
          </div>

          {/* Filters Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-6 rounded-2xl overflow-hidden"
                style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
              >
                {/* Condition Filter */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Condition
                  </label>
                  <select
                    value={filters.condition}
                    onChange={(e) => setFilters(prev => ({ ...prev, condition: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <option value="">All Conditions</option>
                    <option value="Like New">Like New</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                  </select>
                </div>

                {/* Brand Filter */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Brand
                  </label>
                  <select
                    value={filters.brand}
                    onChange={(e) => setFilters(prev => ({ ...prev, brand: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <option value="">All Brands</option>
                    {uniqueBrands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>

                {/* Min Price */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Min Price (₦)
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    value={filters.minPrice}
                    onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `1px solid ${colors.border}`
                    }}
                  />
                </div>

                {/* Max Price */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Max Price (₦)
                  </label>
                  <input
                    type="number"
                    placeholder="1000000"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `1px solid ${colors.border}`
                    }}
                  />
                </div>

                {/* Stock Filter */}
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.inStock}
                      onChange={(e) => setFilters(prev => ({ ...prev, inStock: e.target.checked }))}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: colors.primary }}
                    />
                    <span className="text-sm font-semibold" style={{ color: colors.text }}>
                      In Stock Only
                    </span>
                  </label>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Results Count */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mb-6"
        >
          <p style={{ color: colors.textSecondary }}>
            Showing {filteredPhones.length} of {phones.length} phones
          </p>
        </motion.div>

        {/* Phones Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl overflow-hidden animate-pulse"
                style={{ backgroundColor: colors.bgSecondary }}
              >
                <div className="h-64" style={{ backgroundColor: colors.bgTertiary }} />
                <div className="p-6 space-y-4">
                  <div className="h-4 rounded-full w-3/4" style={{ backgroundColor: colors.bgTertiary }} />
                  <div className="h-6 rounded-full w-1/2" style={{ backgroundColor: colors.bgTertiary }} />
                  <div className="h-8 rounded-full w-2/3" style={{ backgroundColor: colors.bgTertiary }} />
                </div>
              </div>
            ))}
          </div>
        ) : filteredPhones.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16 rounded-2xl"
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <Smartphone size={64} className="mx-auto mb-4 opacity-30" style={{ color: colors.textSecondary }} />
            <h3 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
              No phones found
            </h3>
            <p style={{ color: colors.textSecondary }}>
              Try adjusting your search or filters
            </p>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredPhones.map((phone) => (
              <motion.div
                key={phone._id}
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group cursor-pointer"
                onClick={() => navigate(`/refurbished/${phone._id}`)}
              >
                <div
                  className="rounded-2xl overflow-hidden transition-all duration-300 group-hover:shadow-2xl"
                  style={{
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`
                  }}
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <motion.img
                      src={phone.phoneImages[0]}
                      alt={phone.phoneName}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      whileHover={{ scale: 1.1 }}
                    />
                    
                    {/* Overlay Gradient */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{
                        background: `linear-gradient(to top, ${colors.primary}20, transparent)`
                      }}
                    />

                    {/* Condition Badge */}
                    <div className="absolute top-4 left-4">
                      {getConditionBadge(phone.condition)}
                    </div>

                    {/* Stock Status */}
                    <div className="absolute top-4 right-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold ${
                          phone.inStock ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                        }`}
                      >
                        {phone.inStock ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </div>

                    {/* View Details Button */}
                    <motion.div
                      className="absolute bottom-4 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300"
                      initial={{ y: 20 }}
                      whileHover={{ y: 0 }}
                    >
                      <button
                        className="px-6 py-2 rounded-full font-semibold text-white flex items-center gap-2"
                        style={{
                          background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`
                        }}
                      >
                        View Details
                        <ArrowRight size={16} />
                      </button>
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 line-clamp-2" style={{ color: colors.text }}>
                      {phone.phoneName}
                    </h3>

                    <p className="text-sm mb-3 capitalize" style={{ color: colors.textSecondary }}>
                      {phone.phoneBrand} • {phone.phoneModel}
                    </p>

                    {/* Specifications */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {phone.storage && (
                        <div className="flex items-center gap-2">
                          <HardDrive size={14} style={{ color: colors.primary }} />
                          <span className="text-xs" style={{ color: colors.textSecondary }}>
                            {phone.storage}
                          </span>
                        </div>
                      )}
                      {phone.ram && (
                        <div className="flex items-center gap-2">
                          <Zap size={14} style={{ color: colors.primary }} />
                          <span className="text-xs" style={{ color: colors.textSecondary }}>
                            {phone.ram}
                          </span>
                        </div>
                      )}
                      {phone.color && (
                        <div className="flex items-center gap-2">
                          <Palette size={14} style={{ color: colors.primary }} />
                          <span className="text-xs" style={{ color: colors.textSecondary }}>
                            {phone.color}
                          </span>
                        </div>
                      )}
                      {phone.batteryCapacity && (
                        <div className="flex items-center gap-2">
                          <Battery size={14} style={{ color: colors.primary }} />
                          <span className="text-xs" style={{ color: colors.textSecondary }}>
                            {phone.batteryCapacity}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Warranty */}
                    {phone.warrantyMonths > 0 && (
                      <div className="flex items-center gap-2 mb-4">
                        <Shield size={14} style={{ color: colors.primary }} />
                        <span className="text-xs font-semibold" style={{ color: colors.primary }}>
                          {phone.warrantyMonths} Month Warranty
                        </span>
                      </div>
                    )}

                    {/* Pricing */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-black" style={{ color: colors.primary }}>
                          ₦{phone.phonePrice?.toLocaleString()}
                        </div>
                        {phone.originalPrice && phone.originalPrice > phone.phonePrice && (
                          <div className="text-sm line-through" style={{ color: colors.textSecondary }}>
                            ₦{phone.originalPrice?.toLocaleString()}
                          </div>
                        )}
                      </div>

                      {phone.originalPrice && phone.originalPrice > phone.phonePrice && (
                        <div
                          className="px-3 py-1 rounded-full text-xs font-bold text-white"
                          style={{ backgroundColor: colors.accent }}
                        >
                          Save ₦{(phone.originalPrice - phone.phonePrice)?.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RefurbishedPhonesList;